import React, { useState, useRef } from 'react';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import * as S from '../../styles';
import { STEPS } from '../commonStateTaxData';
import {
  step1Questions,
  withholdingInitialQuestions,
  additionalAmountInitialQuestion,
} from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function Version2025({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  companyStaff,
  getPdfUrl,
  getInitialQuestions,
  resetInitialQuestions,
  formatFullName,
  version,
  formLink,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  prefilledData.employee_name = formatFullName();

  // Personal Info Questions
  const step1Form = useRef();
  const step1InitialQuestions = getInitialQuestions(step1Questions);
  const [stepOneQuestions, setStepOneQuestions] = useState(step1InitialQuestions);

  // Q1 (a,b,c) - Withholding Questions
  const withholdingForm = useRef();
  const withholdingInit = getInitialQuestions(withholdingInitialQuestions);
  const [withholdingQuestions, setWithholdingQuestions] = useState(withholdingInit);

  // Q2 - Additional Amount Questions
  const additionalAmountForm = useRef();
  const additionalAmountInit = getInitialQuestions(additionalAmountInitialQuestion);
  const [additionalAmountQuestions, setAdditionalAmountQuestions] = useState(additionalAmountInit);

  const [isExemptFromWithholding, setIsExemptFromWithholding] = useState(data.is_exempt || false);
  const [exemptionsFromWithholding, setExemptionsFromWithholding] = useState(data.exemption || '');
  const [exemptionsError, setExemptionsError] = useState(false);

  const renderStep1Form = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={stepOneQuestions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = stepOneQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setStepOneQuestions(questions);
        }}
        ref={step1Form}
      />
      <Flex column>
        <S.QuestionLabel mb="7px" isRequired>
          I wish to be exempt from withholding.
        </S.QuestionLabel>
        <S.QuestionWrapper mb="20px">
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={isExemptFromWithholding}
                checked={isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              Yes
            </Flex>
          </label>
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={!isExemptFromWithholding}
                checked={!isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              No
            </Flex>
          </label>
        </S.QuestionWrapper>
      </Flex>
    </div>
  );

  const renderWithholdingQuestions = (isReviewStep) => (
    <FormConsumer
      questions={withholdingQuestions}
      {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
      onQuestionChange={({ question, value }) => {
        const questions = withholdingQuestions.map((q) => {
          if (q.id !== question.id) return q;
          const answer = {
            ...q.answer,
            answer: {
              ...q.answer.answer,
              [q.id]: value,
            },
          };
          const updatedQuestion = { ...q, answer };
          return updatedQuestion;
        });
        setWithholdingQuestions(questions);
      }}
      ref={withholdingForm}
    />
  );

  const renderAdditionalAmountQuestions = (isReviewStep) => (
    <FormConsumer
      questions={additionalAmountQuestions}
      {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
      onQuestionChange={({ question, value }) => {
        const questions = additionalAmountQuestions.map((q) => {
          if (q.id !== question.id) return q;
          const answer = {
            ...q.answer,
            answer: {
              ...q.answer.answer,
              [q.id]: value,
            },
          };
          const updatedQuestion = { ...q, answer };
          return updatedQuestion;
        });
        setAdditionalAmountQuestions(questions);
      }}
      ref={additionalAmountForm}
    />
  );

  const renderExemptionsFromWithholding = (isReviewStep) => (
    <Flex column>
      <S.QuestionLabel mb="10px" isRequired={isExemptFromWithholding}>
        Exemptions from withholding
      </S.QuestionLabel>
      <S.QuestionWrapper mb="24px">
        <label>
          <Flex mb="10px">
            <S.Input
              style={{ height: '22px' }}
              type="radio"
              value="exemption_from_withholding_one"
              checked={exemptionsFromWithholding === 'exemption_from_withholding_one'}
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setExemptionsFromWithholding(e.target.value);
              }}
            />
            <Box ml={2} />
            <Flex column flex={1}>
              <Text mb="15px">
                I claim exemption from withholding for 2025, and I certify
                I meet both of the conditions for exemption:
              </Text>
              <Text mb="8px">1. You did not owe any federal/state income tax last year.</Text>
              <Text>2. You do not expect to owe any federal/state income tax this year.</Text>
            </Flex>
          </Flex>
        </label>
        <label>
          <Flex>
            <S.Input
              style={{ height: '22px' }}
              type="radio"
              value="exemption_from_withholding_two"
              checked={exemptionsFromWithholding === 'exemption_from_withholding_two'}
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setExemptionsFromWithholding(e.target.value);
              }}
            />
            <Box ml={2} />
            <Flex column flex={1}>
              <Text>I certify under penalty of perjury that I am not subject to California withholding. I meet the conditions set forth under the Service Member Civil Relief Act, as amended by the Military Spouses Residency Relief Act and the Veterans Benefits and Transition Act of 2018.</Text>
            </Flex>
          </Flex>
        </label>
        {exemptionsError && exemptionsFromWithholding === '' && (
          <Flex justifyContent="flex-start" mt={1}>
            <Text color="red">This is a mandatory field.</Text>
          </Flex>
        )}
      </S.QuestionWrapper>
    </Flex>
  );

  const handleValidation = () => {
    if (step1Form && step1Form.current && step1Form.current.validate((error) => !error)) {
      return true;
    }

    if (!isExemptFromWithholding) {
      if (withholdingForm && withholdingForm.current
        && withholdingForm.current.validate((error) => !error)) {
        return true;
      }
      if (additionalAmountForm && additionalAmountForm.current
        && additionalAmountForm.current.validate((error) => !error)) {
        return true;
      }
    } else if (exemptionsFromWithholding === '') {
      setExemptionsError(true);
      return true;
    }

    setExemptionsError(false);
    return false;
  };

  const handleSubmission = () => {
    const hasErrors = handleValidation();
    if (hasErrors) return;

    // Initialize data
    const stepOneData = stepOneQuestions
      .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
    let withholdingTempData = resetInitialQuestions(withholdingQuestions)
      .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
    let additionalAmountTempData = resetInitialQuestions(additionalAmountQuestions)
      .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
    let exemptionTempData = '';

    if (!isExemptFromWithholding) {
      withholdingTempData = withholdingQuestions
        .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
      additionalAmountTempData = additionalAmountQuestions
        .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
    } else {
      exemptionTempData = exemptionsFromWithholding;
    }

    const exemptionData = {
      exemption: exemptionTempData,
      is_exempt: isExemptFromWithholding,
    };

    const payload = {
      ...stepOneData,
      ...withholdingTempData,
      ...additionalAmountTempData,
      ...exemptionData,
    };

    saveFormData(payload, () => {
      setCurrentStep(STEPS.step2);
      scrollTop();
    });
  };

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderStep1Form()}
          {isExemptFromWithholding && renderExemptionsFromWithholding()}
          {!isExemptFromWithholding && renderWithholdingQuestions()}
          {!isExemptFromWithholding && renderAdditionalAmountQuestions()}
        </Flex>
        {viewResultAsManager && companyStaff.pii_permission === 'full_access' && (
          <Flex column mt={10} mb={10}>
            <Flex alignItems="center">
              <Text mr={16}>Download signed documents</Text>
              <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                <img src="/j/images/onboarding/download.svg" />
              </a>
            </Flex>
          </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={handleSubmission}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderStep1Form(true)}
        {isExemptFromWithholding && renderExemptionsFromWithholding(true)}
        {!isExemptFromWithholding && renderWithholdingQuestions(true)}
        {!isExemptFromWithholding && renderAdditionalAmountQuestions(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" style={{ height: '640px' }} />
      </Flex>
    </Flex>
  );

  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>

  );
}

export default Version2025;
